
import { Component, Vue, Prop, Emit, Inject, Watch } from 'vue-property-decorator';
import MultipleSelect from '../../../../core/components/Generics/MultipleSelect/MultipleSelect.vue';
import Input from '../../../../core/components/Generics/Input.vue';
import LoadingIndicator from '../../../../core/components/Generics/LoadingIndicator.vue';
import Button from '../../../../core/components/Generics/Button.vue';
import LoadedPolygonsTargetOption from '../LoadedPolygonsTargetOption.vue';
import PointSearch from '../../../../core/components/Map/PointSearch.vue';
import { LatLngPoint } from '../../../../core/utils/point-coordinates';

import { EventBus, TypeEventBus } from '../../../../core/utils';
import { FilterOption, ItemPort, PreloadedFilter, PreloadedFilterOptions, PreloadedItemResponse } from '../../../../core/api/ports';
import { CustomerContext } from '../../../../core/auth/CustomerContext';
import { InjectionKey } from '../../../../core/injection-keys';
import SimpleMap from '../../../../core/components/Map/SimpleMap.vue';
import { MapType } from '../../../../core/components/Map/map-type';
import { FeatureCollection, MultiPolygon } from '../../../../shared/types/MapShapes';
import { filterFeaturesById } from '../../../../shared/helpers/geojson';
import { showErrorSnackbar } from '../../../../shared/helpers/alerts-popups';
import {
  addOption,
  byPreloadedFilter,
  keepSelectedItems,
  PRELOADED_FILTERS,
  PreloadedFiltersSelection,
  preselectMostRecent,
  removeOption,
  toItemsFilters
} from './preloaded-filters';

/** Textos de cada filtro (label, placeholder y listas vacías). */
interface FilterTexts {
  label: string;
  placeholder: string;
  noOptions: string;
  noResults: string;
}

const FILTER_TEXTS: Record<PreloadedFilter, FilterTexts> = {
  [PreloadedFilter.Campaign]: {
    label: 'Campaña:',
    placeholder: 'Todas',
    noOptions: 'No hay campañas disponibles',
    noResults: 'No hay campañas que coincidan con la búsqueda'
  },
  [PreloadedFilter.CropType]: {
    label: 'Tipo de cultivo:',
    placeholder: 'Todos',
    noOptions: 'No hay tipos de cultivo disponibles',
    noResults: 'No hay tipos de cultivo que coincidan con la búsqueda'
  },
  [PreloadedFilter.Department]: {
    label: 'Departamento:',
    placeholder: 'Todos',
    noOptions: 'No hay departamentos disponibles',
    noResults: 'No hay departamentos que coincidan con la búsqueda'
  }
};

@Component({
  components: {
    Button,
    MultipleSelect,
    LoadedPolygonsTargetOption,
    SimpleMap,
    LoadingIndicator,
    Input,
    PointSearch
  }
})
export default class ModalListItems extends Vue {
  @Inject(InjectionKey.ItemApi) readonly itemApi!: ItemPort;
  @Inject(InjectionKey.Customer) readonly customerContext!: CustomerContext;
  /** "Buscar por punto (lat/lng)" sobre el mapa (`PolygonTool.PointSearch` del tenant; lo decide el padre). */
  @Prop({ default: false }) readonly pointSearchEnabled!: boolean;
  /** `id` del item que devolvió `GET /ranch/` (no el `:id` de la URL): va como `ranch_id` en los WS del listado. */
  @Prop({ type: Number, default: null }) readonly ranchId!: number | null;
  /** Último punto buscado: se vuelve a marcar cuando el mapa se recrea (cambio de filtros). */
  lastSearchPoint: LatLngPoint | null = null;
  isSexagesimal: boolean = false;
  visible: boolean = true;
  modalTitle: string = 'Listado de items precargados';
  selectionHint: string = 'Marque los polígonos que desea seleccionar';
  /** La vista de suscripción arranca en satélite en todos los clientes (no depende de `features.satelliteMapByDefault`). */
  mapType: MapType = MapType.Hybrid;
  /** Opciones de cada filtro; un filtro sin opciones queda deshabilitado. */
  filterOptions: PreloadedFilterOptions = byPreloadedFilter(() => []);
  /** Selección de cada filtro; vacía = todos. Campaña arranca con la más reciente, el resto vacío. */
  selected: PreloadedFiltersSelection = byPreloadedFilter(() => []);
  /** Opciones de los filtros en curso (un solo request para los tres); los lotes se cargan cuando termina. */
  loadingFilterOptions: boolean = false;
  /** Ya se pidieron lotes al menos una vez: desde ahí se muestran tabla y mapa. */
  hasRequestedItems: boolean = false;
  /** Contador de requests de lotes: solo se aplica la respuesta del último (evita carreras entre cambios rápidos). */
  polygonsRequestId: number = 0;
  openDropdownCount: number = 0;
  polygonsList: Array<PreloadedItemResponse> = [];
  loadingPolygons: boolean = false;
  selectedItems: Array<PreloadedItemResponse> = [];
  yearMultiPolygon: MultiPolygon | null = null;
  /** Límites de los departamentos que devuelve `/ranch/list/`, dibujados como el `department_polygon` del item. */
  departmentsLayer: FeatureCollection | null = null;
  overwriteGeneralMap: boolean = false;
  search: string = '';
  headers: any[] = [
    {
      text: 'Nombre',
      value: 'name'
    },
    {
      text: 'Campaña',
      value: 'campaign'
    },
    {
      text: 'Acciones',
      value: 'actions',
      width: '40px',
      sortable: false
    }
  ];

  /** Filtros en el orden en que se muestran (campaña, tipo de cultivo, departamento). Constante: no va en `data`. */
  get filters(): PreloadedFilter[] {
    return PRELOADED_FILTERS;
  }

  get filterTexts(): Record<PreloadedFilter, FilterTexts> {
    return FILTER_TEXTS;
  }

  get areSelectedItems() {
    return this.selectedItems.length > 0;
  }

  /**
   * Modal a pantalla completa: el mapa ocupa el alto disponible (pantalla menos encabezado, filtros y botones), con
   * un mínimo para pantallas bajas.
   */
  get mapHeight(): string {
    return 'max(400px, calc(100vh - 300px))';
  }

  /** Mientras un dropdown está abierto, las opciones pueden sobresalir del modal. */
  get isDropdownOpen(): boolean {
    return this.openDropdownCount > 0;
  }

  /**
   * Aseguradora activa para `customer`, o `null` (contexto deshabilitado o `/me/` sin respuesta → no se manda).
   * Se lee en cada request, nunca en un inicializador de campo (regla 8).
   */
  get activeCustomerId(): number | null {
    return this.customerContext ? this.customerContext.customerId : null;
  }

  created() {
    // En una ruta por token el `/me/` puede seguir en curso: se espera a que se resuelva para que la primera
    // request ya lleve `customer`.
    if (!this.customerContext || this.customerContext.resolved) {
      this.loadFilterOptions();
      return;
    }
    this.loadingFilterOptions = true;
    const unwatch = this.$watch(
      () => this.customerContext.resolved,
      (resolved: boolean) => {
        if (!resolved) return;
        unwatch();
        this.loadFilterOptions();
      }
    );
  }

  /**
   * Opciones de los tres filtros (un solo request). Campaña arranca con la más reciente y el resto vacío (= todos).
   * Si falla, los filtros quedan vacíos y deshabilitados. En ambos casos después se cargan los lotes.
   */
  loadFilterOptions() {
    this.loadingFilterOptions = true;
    this.itemApi.getPreloadedFilterOptions(this.activeCustomerId, this.ranchId)
      .then((options) => {
        this.filterOptions = options;
        if (options[PreloadedFilter.Campaign].length === 0) {
          showErrorSnackbar('No hay campañas disponibles para el listado de items');
        }
      })
      .catch(() => {
        this.filterOptions = byPreloadedFilter(() => []);
        showErrorSnackbar('No se pudieron cargar los filtros del listado de items');
      })
      .finally(() => {
        this.loadingFilterOptions = false;
        this.selected = byPreloadedFilter(() => []);
        this.selected[PreloadedFilter.Campaign] = preselectMostRecent(this.filterOptions[PreloadedFilter.Campaign]);
        this.loadPreloadedItems();
      });
  }

  onSelectOption(filter: PreloadedFilter, option: FilterOption) {
    const next = addOption(this.selected[filter], option);
    if (next === this.selected[filter]) return;
    this.setSelected(filter, next);
  }

  onRemoveOption(filter: PreloadedFilter, option: FilterOption) {
    this.setSelected(filter, removeOption(this.selected[filter], option));
  }

  onDropdownOpen() {
    this.openDropdownCount++;
  }

  onDropdownClose() {
    if (this.openDropdownCount > 0) {
      this.openDropdownCount--;
    }
  }

  setSelected(filter: PreloadedFilter, options: FilterOption[]) {
    this.selected[filter] = options;
    this.loadPreloadedItems();
  }

  /**
   * Lotes que cumplen los tres filtros; un filtro vacío = todos (se omite su parámetro). Se conservan los marcados
   * que sigan en el resultado nuevo.
   */
  loadPreloadedItems() {
    const requestId = ++this.polygonsRequestId;

    this.hasRequestedItems = true;
    this.yearMultiPolygon = null;
    this.departmentsLayer = null;
    this.loadingPolygons = true;

    this.itemApi.getPreloadedItems(toItemsFilters(this.selected), this.activeCustomerId, this.ranchId)
      .then((response) => {
        if (requestId !== this.polygonsRequestId) return;
        this.polygonsList = response.list || [];
        this.yearMultiPolygon = response.polygons || null;
        this.departmentsLayer = response.departments || null;
        this.selectedItems = keepSelectedItems(this.selectedItems, this.polygonsList);
      })
      .catch(() => {
        if (requestId !== this.polygonsRequestId) return;
        this.polygonsList = [];
        this.selectedItems = [];
        showErrorSnackbar('No se pudieron cargar los items con los filtros seleccionados');
      })
      .finally(() => {
        if (requestId === this.polygonsRequestId) {
          this.loadingPolygons = false;
        }
      });
  }

  /**
   * El mapa se vuelve a crear con cada carga: al quedar listo se encuadra sobre los lotes resultantes ("centrar en"
   * un departamento = filtrarlo) y se marcan los que siguen seleccionados. Si hay un punto buscado, se vuelve a
   * marcar y el mapa centra en él en lugar de encuadrar los lotes (lo pidió el usuario explícitamente).
   */
  syncMapSelection() {
    const map = this.getMapRef();
    if (map && this.lastSearchPoint) {
      map.showSearchPoint(this.lastSearchPoint);
    } else if (map && this.polygonsList.length > 0) {
      map.fitMapToCurrentFeatures();
    }
    if (this.selectedItems.length > 0) {
      this.onSelectedItems(this.selectedItems);
    }
  }

  selectRanchsInTable(selectedFeaturesInMap: Array<google.maps.Data.Feature>) {
    const incomingIds = selectedFeaturesInMap
      .map(f => f.getProperty('id') || f.getProperty('customId'))
      .filter(id => id != null) as Array<number|string>;

    this.selectedItems = this.selectedItems.filter(item =>
      incomingIds.includes(item.id)
    );

    incomingIds.forEach(id => {
      const already = this.selectedItems.some(item => item.id === id);
      if (!already) {
        const match = this.polygonsList.find(item => item.id === id);
        if (match) {
          this.selectedItems.push(match);
        }
      }
    });
  }

  onSelectedItems(selectedItems: Array<PreloadedItemResponse>){
    const map = this.getMapRef();
    if(map){
      map.selectFeaturesFromIds(
        selectedItems.map(item => item.id),
        false,
        true
      );
    }
  }

  onEyeClicked(item: PreloadedItemResponse){
    const map = this.getMapRef();
    if(map){
      map.focusPolygonInMapById(item.id);
    }
    
  }

  onPointSearch(point: LatLngPoint) {
    this.lastSearchPoint = point;
    const map = this.getMapRef();
    if (map) {
      map.showSearchPoint(point);
    }
  }

  clearPointSearch() {
    this.lastSearchPoint = null;
    const map = this.getMapRef();
    if (map) {
      map.clearSearchPoint();
    }
  }

  /**
   * Mapa actual del modal. Es un método y no un getter a propósito: en vue-class-component un getter es una computed
   * cacheada, y `$refs` no es reactivo. Como el mapa se recrea en cada carga (`v-if`), un getter quedaba apuntando al
   * mapa viejo (el ojo no centraba, y "Guardar" podía leer la selección del mapa destruido).
   */
  getMapRef(): SimpleMap | undefined {
    return this.$refs.simpleMapRef as SimpleMap | undefined;
  }

  clickSaveItems(){
    if(!this.areSelectedItems){
      EventBus.$emit(TypeEventBus.SHOW_MESSAGE, 'Debe marcar un polígono en el Listado de Items', 'error');
      return
    }
    const map = this.getMapRef();
    if (!map) return;
    let selectedPolys = map.selectedRanchs as Array<google.maps.Data.Feature>;
    let selectedIds = selectedPolys.map(poly => poly.getProperty('id'));
   
    let filteredFeatures = filterFeaturesById(this.yearMultiPolygon, selectedIds, 'id');
    this.changePolygonInMap(filteredFeatures, this.overwriteGeneralMap);
    this.closeModal();
    
  }



  @Emit()
  closeModal() {}

  @Emit()
  changePolygonInMap(geojson, overwrite: boolean = false) { return }

 
}

